import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform } from 'react-native';

import { useOnboardingFinished } from '@hooks/useOnboardingActive';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import { AsyncStorage } from '@utils/storage';

const STORAGE_KEY_ACTIVATED_AT = 'taskNudgeActivatedAt';
const STORAGE_KEY_DISMISS_COUNT = 'taskNudgeDismissCount';
const STORAGE_KEY_LAST_DISMISS_AT = 'taskNudgeLastDismissAt';
const STORAGE_KEY_COMPLETED = 'taskNudgeCompleted';

const INITIAL_DELAY_MS = 10 * 60 * 1000; // 10 min after project creation
const RETRY_DELAYS_MS = [24 * 60 * 60 * 1000, 48 * 60 * 60 * 1000];
const MAX_DISMISSES = 2;

const isNative = Platform.OS !== 'web';

type TaskNudgeContextType = {
  isVisible: boolean;
  activateNudge: () => Promise<void>;
  dismissNudge: () => Promise<void>;
  completeNudge: () => Promise<void>;
};

const TaskNudgeContext = createContext<TaskNudgeContextType | undefined>(
  undefined
);

export const TaskNudgeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { trackEvent } = useMixpanel();
  // TA-10920 #19: Smart Nudges only surface once onboarding has finished.
  const onboardingFinished = useOnboardingFinished();
  const [activatedAt, setActivatedAt] = useState<number | null>(null);
  const [dismissCount, setDismissCount] = useState(0);
  const [lastDismissAt, setLastDismissAt] = useState<number | null>(null);
  const [completed, setCompleted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isNative) {
      setLoaded(true);
      return;
    }
    const loadState = async () => {
      const [storedActivatedAt, storedCount, storedLastAt, storedCompleted] =
        await Promise.all([
          AsyncStorage.getItem(STORAGE_KEY_ACTIVATED_AT),
          AsyncStorage.getItem(STORAGE_KEY_DISMISS_COUNT),
          AsyncStorage.getItem(STORAGE_KEY_LAST_DISMISS_AT),
          AsyncStorage.getItem(STORAGE_KEY_COMPLETED),
        ]);
      if (storedActivatedAt)
        setActivatedAt(parseInt(storedActivatedAt, 10) || null);
      if (storedCount) setDismissCount(parseInt(storedCount, 10) || 0);
      if (storedLastAt) setLastDismissAt(parseInt(storedLastAt, 10) || null);
      if (storedCompleted === 'true') setCompleted(true);
      setLoaded(true);
    };
    loadState();
  }, []);

  // Schedule re-render at exact moment the nudge becomes visible
  useEffect(() => {
    if (!activatedAt || completed) return;

    let msUntilShow: number;

    if (dismissCount === 0) {
      msUntilShow = activatedAt + INITIAL_DELAY_MS - Date.now();
    } else if (dismissCount <= MAX_DISMISSES && lastDismissAt) {
      const delay = RETRY_DELAYS_MS[dismissCount - 1];
      msUntilShow = lastDismissAt + delay - Date.now();
    } else {
      return;
    }

    if (msUntilShow <= 0) {
      setNow(Date.now());
      return;
    }
    const t = setTimeout(() => setNow(Date.now()), msUntilShow + 100);
    return () => clearTimeout(t);
  }, [activatedAt, completed, dismissCount, lastDismissAt]);

  const isVisible = useMemo(() => {
    if (!onboardingFinished) return false;
    if (!loaded || !activatedAt || completed) return false;
    if (dismissCount > MAX_DISMISSES) return false;

    if (dismissCount === 0) {
      return now - activatedAt >= INITIAL_DELAY_MS;
    }
    if (!lastDismissAt) return true;
    const delay = RETRY_DELAYS_MS[dismissCount - 1];
    return now - lastDismissAt >= delay;
  }, [
    onboardingFinished,
    loaded,
    activatedAt,
    completed,
    dismissCount,
    lastDismissAt,
    now,
  ]);

  const activateNudge = useCallback(async () => {
    if (!isNative) return;
    // Don't reset if already active (preserves retry state)
    if (activatedAt !== null && !completed) return;
    const ts = Date.now();
    setActivatedAt(ts);
    setDismissCount(0);
    setLastDismissAt(null);
    setCompleted(false);
    setNow(ts);
    await Promise.all([
      AsyncStorage.setItem(STORAGE_KEY_ACTIVATED_AT, String(ts)),
      AsyncStorage.deleteItem(STORAGE_KEY_DISMISS_COUNT),
      AsyncStorage.deleteItem(STORAGE_KEY_LAST_DISMISS_AT),
      AsyncStorage.deleteItem(STORAGE_KEY_COMPLETED),
    ]);
  }, [activatedAt, completed]);

  const dismissNudge = useCallback(async () => {
    if (!isNative) return;
    const next = dismissCount + 1;
    const ts = Date.now();
    setDismissCount(next);
    setLastDismissAt(ts);
    setNow(ts);
    await Promise.all([
      AsyncStorage.setItem(STORAGE_KEY_DISMISS_COUNT, String(next)),
      AsyncStorage.setItem(STORAGE_KEY_LAST_DISMISS_AT, String(ts)),
    ]);
    trackEvent(MIXPANEL_EVENTS.TASK_NUDGE_DISMISSED, { dismiss_count: next });
  }, [dismissCount, trackEvent]);

  const completeNudge = useCallback(async () => {
    if (!isNative) return;
    if (completed) return;
    setCompleted(true);
    await AsyncStorage.setItem(STORAGE_KEY_COMPLETED, 'true');
    trackEvent(MIXPANEL_EVENTS.TASK_NUDGE_COMPLETED);
  }, [completed, trackEvent]);

  const value = useMemo<TaskNudgeContextType>(
    () => ({ isVisible, activateNudge, dismissNudge, completeNudge }),
    [isVisible, activateNudge, dismissNudge, completeNudge]
  );

  return (
    <TaskNudgeContext.Provider value={value}>
      {children}
    </TaskNudgeContext.Provider>
  );
};

export function useTaskNudge(): TaskNudgeContextType {
  const ctx = useContext(TaskNudgeContext);
  if (!ctx) {
    throw new Error('useTaskNudge must be used within TaskNudgeProvider');
  }
  return ctx;
}
